import { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { FuzzySearchOptions, useFuzzySearch } from '@workwhile/utils'
import { createContext } from 'lib/createContext'
import { CompanyPendingInvite } from 'api/company'
import { useCompanyPendingInvites } from 'queries/company'

const PAGE_SIZE = 10

const fuzzySearchOptions: FuzzySearchOptions<CompanyPendingInvite> = {
  keySelector: (invite) => [invite.email ?? ''],
}

// Stable reference so the fuzzy search index isn't rebuilt on every render
// while the invites are still loading.
const NO_INVITES: CompanyPendingInvite[] = []

type PendingInvitesProviderState = {
  state: {
    searchTerm: string
    pageIndex: number
    invitesPage: CompanyPendingInvite[]
    totalPages: number
    isLoading: boolean
    isEmpty: boolean
    hasNoSearchResults: boolean
  }
  actions: {
    setSearchTerm: (searchTerm: string) => void
    nextPage: () => void
    previousPage: () => void
  }
}

export const [PendingInvitesContext, usePendingInvitesContext] =
  createContext<PendingInvitesProviderState>({
    name: 'PendingInvitesContext',
  })

export function PendingInvitesProvider({ children }: PropsWithChildren) {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [pageIndex, setPageIndex] = useState<number>(0)

  const { data, isLoading } = useCompanyPendingInvites()
  const allInvites = data ?? NO_INVITES
  const fuzzySearch = useFuzzySearch(fuzzySearchOptions, allInvites)

  useEffect(() => {
    // Reset page index when search term changes
    setPageIndex(0)
  }, [searchTerm])

  const value = useMemo(() => {
    const { results: filteredInvites } = fuzzySearch(searchTerm.trim())

    const totalPages = Math.ceil(filteredInvites.length / PAGE_SIZE)
    const invitesPage = filteredInvites.slice(
      pageIndex * PAGE_SIZE,
      (pageIndex + 1) * PAGE_SIZE
    )

    const isEmpty = !isLoading && allInvites.length === 0
    const hasNoSearchResults =
      !isLoading && allInvites.length > 0 && filteredInvites.length === 0

    const handlePreviousPage = () => {
      setPageIndex((prev) => (prev > 0 ? prev - 1 : 0))
    }

    const handleNextPage = () => {
      setPageIndex((prev) => (prev < totalPages - 1 ? prev + 1 : prev))
    }

    return {
      state: {
        searchTerm,
        pageIndex,
        invitesPage,
        totalPages,
        isLoading,
        isEmpty,
        hasNoSearchResults,
      },
      actions: {
        setSearchTerm,
        nextPage: handleNextPage,
        previousPage: handlePreviousPage,
      },
    } as PendingInvitesProviderState
  }, [searchTerm, pageIndex, allInvites, isLoading, fuzzySearch])

  return (
    <PendingInvitesContext.Provider value={value}>
      {children}
    </PendingInvitesContext.Provider>
  )
}
